import React from 'react';
import { Role } from 'models/Role';
import DashboardIcon from '@material-ui/icons/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import SettingsIcon from '@material-ui/icons/Settings';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ContactsIcon from '@material-ui/icons/Contacts';
import TodayIcon from '@material-ui/icons/Today';
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import CategoryIcon from '@material-ui/icons/Category';
import ListAltIcon from '@material-ui/icons/ListAlt';
import TitleIcon from '@mui/icons-material/Title';
import UsageIcon from '@material-ui/icons/DataUsage';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { FeatureFlag, OrganizationPricingPlan } from 'api/organizationApi';
import DescriptionIcon from '@mui/icons-material/Description';
import ConstructionIcon from '@mui/icons-material/Construction';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SummarizeIcon from '@mui/icons-material/Summarize';

export interface SidebarItem {
  title: string;
  icon: JSX.Element;
  open?: boolean;
  href?: string;
  restrictTo?: Role[];
  // allowedPlans?: OrganizationPricingPlan[];
  specialFeatures?: FeatureFlag[];
  nestedItems?: {
    title: string;
    href: string;
    icon: JSX.Element;
    // allowedPlans: OrganizationPricingPlan[];
    specialFeature?: FeatureFlag;
    featureFlag?: FeatureFlag;
    /** Hides the item from roles outside this list, like the top-level
     *  `restrictTo`. E.g. KPI Performance is administrator-only. */
    restrictTo?: Role[];
  }[];
  featureFlag?: FeatureFlag;
}

export const sidebarItems: Array<SidebarItem & { 'data-cy'?: string }> = [
  {
    title: 'Dashboard',
    href: '/',
    icon: <DashboardIcon data-cy={'dashboard-icon'} />,
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor', 'Technician'],
    // Add the data-cy attribute here
  },
  {
    title: 'Administration',
    icon: <SettingsIcon data-cy={'setting-icon'} />,
    href: '/administration',
    restrictTo: ['Administrator'],
    open: false,
    nestedItems: [
      {
        title: 'Organization',
        href: '/administration/organization',
        icon: <BusinessIcon data-cy={'business-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Users',
        href: '/administration/users',
        icon: <PeopleIcon data-cy={'user-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Form Templates',
        href: '/administration/form-templates',
        icon: <ListAltIcon data-cy={'form-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Letterheads',
        href: '/administration/letterheads',
        icon: <TitleIcon data-cy={'letterhead-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Moisture Meters',
        href: '/administration/moisture-meters',
        icon: <SpeedIcon data-cy={'letterhead-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Usage & Billing',
        href: '/administration/billing',
        icon: <UsageIcon data-cy={'usage-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
    ],
  },
  {
    title: 'Inbox',
    href: '/inbox',
    icon: <InboxIcon data-cy={'inbox-icon'} />,
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor'],
  },
  {
    title: 'Clients',
    href: '/clients',
    icon: <ContactsIcon data-cy={'clients-icon'} />,
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor'],
  },
  {
    title: 'Jobs',
    href: '/jobs',
    icon: <AssignmentIcon data-cy={'jobs-icon'} />,
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor', 'Technician'],
  },
  {
    title: 'Equipment',
    href: '/equipment',
    icon: <CategoryIcon data-cy={'equipment-icon'} />,
    restrictTo: ['Administrator'],
  },
  {
    title: 'Rate Card',
    href: '/rate-card',
    icon: <LocalOfferIcon data-cy={'rate-card-icon'} />,
    restrictTo: ['Administrator'],
    open: false,
    // allowedPlans: ['UNRESTRICTED'],
    nestedItems: [
      {
        title: 'Labour',
        href: '/rate-card/labour',
        icon: <GroupsIcon data-cy={'people-icon'} />,
        // allowedPlans: ['UNRESTRICTED'],
        featureFlag: FeatureFlag.RATE_CARD_LABOUR_RATE,
      },
      {
        title: 'Equipment',
        href: '/rate-card/equipment',
        icon: <ConstructionIcon data-cy={'construction-icon'} />,
        // allowedPlans: ['UNRESTRICTED'],
        featureFlag: FeatureFlag.RATE_CARD_EQUIPMENT,
      },
      {
        title: 'Consumables',
        href: '/rate-card/consumables',
        icon: <AssessmentIcon data-cy={'assessment-icon'} />,
        // allowedPlans: ['UNRESTRICTED'],
        featureFlag: FeatureFlag.RATE_CARD_CONSUMABLE,
      },
      {
        title: 'Other Expenses',
        href: '/rate-card/other-expenses',
        icon: <ReceiptIcon data-cy={'receipt-icon'} />,
        featureFlag: FeatureFlag.RATE_CARD_OTHER_EXPENSES,
      },
      {
        title: 'Margin Summary',
        href: '/rate-card/margin-summary',
        icon: <SummarizeIcon data-cy={'margin-summary-icon'} />,
        // allowedPlans: ['UNRESTRICTED'],
        featureFlag: FeatureFlag.RATE_MARGIN_SUMMARY,
      },
    ],
  },
  {
    title: 'Reports',
    icon: <DescriptionIcon data-cy={'reports-icon'} />,
    href: '/reports',
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor'],
    open: false,
    nestedItems: [
      {
        title: 'Job Reports',
        href: '/reports/job-reports',
        icon: <AssessmentIcon data-cy={'assessment-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
      },
      {
        title: 'Labour Reports',
        href: '/reports/labour-reports',
        icon: <AssessmentIcon data-cy={'assessment-icon'} />,
        // allowedPlans: ['FREE', 'PAYG', 'UNRESTRICTED'],
        featureFlag: FeatureFlag.LABOUR_REPORT,
      },
      // KPI Performance is hidden until the tab is re-enabled — see
      // `canViewKpi` in `ReportsPage.tsx`.
    ],
  },
  {
    title: 'Calendar',
    href: '/calendar',
    icon: <TodayIcon data-cy={'calendar-icon'} />,
    restrictTo: ['Administrator', 'ProjectManager', 'Supervisor', 'Technician'],
  },
];
